
import React from 'react';
import { useAuth } from '@/contexts/SupabaseAuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requiredRole?: string | string[];
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  fallback,
  requiredRole 
}) => {
  const { isLoading, isAuthenticated, user, hasRole } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return fallback || (
      <div className="flex items-center justify-center min-h-screen bg-background-primary">
        <div className="max-w-md w-full mx-4">
          <div className="bg-background-secondary border border-border-primary rounded-xl p-8 text-center space-y-6">
            <div className="space-y-2">
              <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
                <Loader2 className="h-8 w-8 text-primary" />
              </div>
              <h2 className="text-2xl font-bold text-text-primary">Authentication Required</h2>
              <p className="text-text-secondary">
                Please sign in to access this page and unlock all features.
              </p>
            </div>
            
            <div className="space-y-3">
              <a
                href="/login"
                className="w-full inline-flex items-center justify-center gap-2 h-10 px-4 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
              >
                Sign In
              </a>
              <a
                href="/signup"
                className="w-full inline-flex items-center justify-center gap-2 h-10 px-4 border border-border-primary text-text-primary rounded-lg font-medium hover:bg-background-tertiary transition-colors"
              >
                Create Account
              </a>
              <a
                href="/"
                className="w-full inline-flex items-center justify-center gap-2 h-8 px-3 text-sm text-text-tertiary hover:text-text-primary transition-colors"
              >
                ← Back to Home
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (requiredRole && user) {
    const hasRequiredRole = hasRole(requiredRole);

    if (!hasRequiredRole) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
            <p className="text-muted-foreground">You don't have permission to access this page.</p>
          </div>
        </div>
      );
    }
  }

  return <>{children}</>;
};
